import { supabase } from '../lib/supabase';
import Papa from 'papaparse';
import JSZip from 'jszip';

export interface BackupMetadata {
  version: string;
  timestamp: string;
  totalTables: number;
  totalRecords: number;
}

export interface BackupResult {
  success: boolean;
  metadata: BackupMetadata;
  errors: string[];
}

export interface RestoreResult {
  success: boolean;
  tablesRestored: number;
  recordsRestored: number;
  errors: string[];
}

// Define all tables to backup in dependency order (children before parents for restore)
const BACKUP_TABLES = [
  // Core system tables
  'courses',
  'instructors',
  'customers',
  'locations',
  'contacts',

  // Booking related (parent first, then children)
  'bookings',
  'booking_dates',
  'booking_attendees',
  'booking_day_prices',
  'public_booking_clients',
  'public_booking_client_attendees',

  // Certificate and reminders
  'certificate_reminders',
  'certificate_submissions',
  'review_reminders',
  'customer_reminders',
  'booking_training_reminders',

  // Quotes and invoices
  'quotes',
  'quote_dates',
  'quote_line_items',
  'invoices',
  'invoice_line_items',

  // Notes and communications
  'client_notes',
  'email_templates',
  'whatsapp_templates',
  'email_drafts',
  'email_outbox',
  'email_automation_settings',

  // Admin and system
  'admin_tasks',
  'audit_logs',
  'system_settings',
  'system_announcements',
  'bug_reports',

  // Public forms and attendees
  'public_form_config',
  'public_form_submissions',
  'enrollment_submissions',
  'individual_attendees',

  // Portal and access
  'customer_portal_tokens',
  'portal_notification_preferences',
  'public_form_tokens',

  // Tests (exams) - for HSA / compliance evidence
  'tests',
  'test_questions',
  'test_attempts',

  // Other system tables
  'instructor_availability',
  'feature_visibility_settings',
  'certificate_access_logs',
  'manual_email_contacts',
  'user_dashboard_preferences',
  'custom_dashboard_widgets',
];

// Tables that use a different column for ordering (no created_at)
const BACKUP_ORDER_COLUMN: Record<string, string> = {
  test_attempts: 'submitted_at',
  test_questions: 'order_index',
};

/**
 * Export all system data to a ZIP file containing CSV files for each table
 */
export async function exportAllData(): Promise<Blob> {
  const zip = new JSZip();
  const metadata: BackupMetadata = {
    version: '2.0.0',
    timestamp: new Date().toISOString(),
    totalTables: 0,
    totalRecords: 0,
  };

  const errors: string[] = [];

  console.log('🔄 Starting comprehensive data export...');

  for (const tableName of BACKUP_TABLES) {
    try {
      console.log(`📊 Exporting table: ${tableName}`);

      // Fetch all data from the table (use table-specific order column where needed)
      const orderCol = BACKUP_ORDER_COLUMN[tableName] ?? 'created_at';
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order(orderCol, { ascending: true });

      if (error) {
        console.error(`❌ Error exporting ${tableName}:`, error);
        errors.push(`Failed to export ${tableName}: ${error.message}`);
        continue;
      }

      if (data && data.length > 0) {
        // Convert to CSV
        const csv = Papa.unparse(data);
        zip.file(`${tableName}.csv`, csv);

        metadata.totalTables++;
        metadata.totalRecords += data.length;

        console.log(`✅ Exported ${data.length} records from ${tableName}`);
      } else {
        console.log(`⚠️ No data in ${tableName}, skipping`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`❌ Exception exporting ${tableName}:`, errorMessage);
      errors.push(`Exception exporting ${tableName}: ${errorMessage}`);
    }
  }

  // Add metadata file
  zip.file('backup_metadata.json', JSON.stringify(metadata, null, 2));

  // Add errors log if any
  if (errors.length > 0) {
    zip.file('backup_errors.txt', errors.join('\n'));
  }

  console.log(`✅ Export complete: ${metadata.totalTables} tables, ${metadata.totalRecords} total records`);

  // Generate ZIP blob
  const blob = await zip.generateAsync({ type: 'blob' });
  return blob;
}

/**
 * Import data from a ZIP backup file
 */
export async function importAllData(zipFile: File): Promise<RestoreResult> {
  const result: RestoreResult = {
    success: true,
    tablesRestored: 0,
    recordsRestored: 0,
    errors: [],
  };

  try {
    console.log('🔄 Starting comprehensive data import...');

    // Load and parse ZIP file
    const zip = new JSZip();
    const zipContent = await zip.loadAsync(zipFile);

    // Read metadata if available
    let metadata: BackupMetadata | null = null;
    if (zipContent.files['backup_metadata.json']) {
      const metadataContent = await zipContent.files['backup_metadata.json'].async('text');
      metadata = JSON.parse(metadataContent);
      console.log('📋 Backup metadata:', metadata);
    }

    // Process each table in dependency order
    for (const tableName of BACKUP_TABLES) {
      const fileName = `${tableName}.csv`;

      if (!zipContent.files[fileName]) {
        console.log(`⚠️ No backup file for ${tableName}, skipping`);
        continue;
      }

      try {
        console.log(`📊 Importing table: ${tableName}`);

        // Read CSV content
        const csvContent = await zipContent.files[fileName].async('text');

        // Parse CSV
        const parsedData = await new Promise<any[]>((resolve, reject) => {
          Papa.parse(csvContent, {
            header: true,
            skipEmptyLines: true,
            transformHeader: (header) => header.replace(/^\uFEFF/, '').trim(),
            complete: (results) => resolve(results.data),
            error: (error) => reject(error),
          });
        });

        if (parsedData.length === 0) {
          console.log(`⚠️ No data in ${tableName} CSV, skipping`);
          continue;
        }

        // Transform data (convert string booleans, parse JSON fields, etc.)
        const transformedData = parsedData.map(row => transformRowForImport(row, tableName));

        // Delete existing data
        const { error: deleteError } = await supabase
          .from(tableName)
          .delete()
          .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all except system records

        if (deleteError) {
          console.warn(`⚠️ Warning deleting existing data from ${tableName}:`, deleteError.message);
        }

        // Insert new data in batches (Supabase has a limit)
        const batchSize = 100;
        for (let i = 0; i < transformedData.length; i += batchSize) {
          const batch = transformedData.slice(i, i + batchSize);

          const { error: insertError } = await supabase
            .from(tableName)
            .insert(batch);

          if (insertError) {
            console.error(`❌ Error inserting batch ${i / batchSize + 1} into ${tableName}:`, insertError);
            result.errors.push(`Failed to insert batch into ${tableName}: ${insertError.message}`);
            result.success = false;
          } else {
            result.recordsRestored += batch.length;
          }
        }

        result.tablesRestored++;
        console.log(`✅ Imported ${transformedData.length} records into ${tableName}`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        console.error(`❌ Exception importing ${tableName}:`, errorMessage);
        result.errors.push(`Exception importing ${tableName}: ${errorMessage}`);
        result.success = false;
      }
    }

    console.log(`✅ Import complete: ${result.tablesRestored} tables, ${result.recordsRestored} records restored`);
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('❌ Fatal error during import:', errorMessage);
    result.errors.push(`Fatal error: ${errorMessage}`);
    result.success = false;
  }

  return result;
}

/**
 * Transform a row for import (handle type conversions)
 */
function transformRowForImport(row: any, tableName: string): any {
  const transformed: any = {};

  for (const [key, value] of Object.entries(row)) {
    if (value === '' || value === null || value === undefined) {
      transformed[key] = null;
      continue;
    }

    // Convert string booleans
    if (value === 'true') {
      transformed[key] = true;
    } else if (value === 'false') {
      transformed[key] = false;
    }
    // Parse numbers
    else if (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '') {
      // Check if it looks like a UUID or ID string
      if (key.toLowerCase().includes('id') || value.includes('-')) {
        transformed[key] = value;
      } else {
        transformed[key] = Number(value);
      }
    }
    // Try to parse JSON fields
    else if (typeof value === 'string' && (value.startsWith('{') || value.startsWith('['))) {
      try {
        transformed[key] = JSON.parse(value);
      } catch {
        transformed[key] = value;
      }
    }
    // Keep as-is
    else {
      transformed[key] = value;
    }
  }

  return transformed;
}

/**
 * Get backup statistics
 */
export async function getBackupStatistics(): Promise<{
  tables: { name: string; count: number }[];
  totalRecords: number;
}> {
  const stats = {
    tables: [] as { name: string; count: number }[],
    totalRecords: 0,
  };

  for (const tableName of BACKUP_TABLES) {
    try {
      const { count, error } = await supabase
        .from(tableName)
        .select('*', { count: 'exact', head: true });

      if (!error && count !== null) {
        stats.tables.push({ name: tableName, count });
        stats.totalRecords += count;
      }
    } catch (error) {
      console.error(`Error getting count for ${tableName}:`, error);
    }
  }

  return stats;
}

/**
 * Export specific tables only
 */
export async function exportSelectedTables(tableNames: string[]): Promise<Blob> {
  const zip = new JSZip();
  const metadata: BackupMetadata = {
    version: '2.0.0',
    timestamp: new Date().toISOString(),
    totalTables: 0,
    totalRecords: 0,
  };

  for (const tableName of tableNames) {
    try {
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .order('created_at', { ascending: true });

      if (error) {
        console.error(`Error exporting ${tableName}:`, error);
        continue;
      }

      if (data && data.length > 0) {
        const csv = Papa.unparse(data);
        zip.file(`${tableName}.csv`, csv);

        metadata.totalTables++;
        metadata.totalRecords += data.length;
      }
    } catch (error) {
      console.error(`Exception exporting ${tableName}:`, error);
    }
  }

  zip.file('backup_metadata.json', JSON.stringify(metadata, null, 2));

  const blob = await zip.generateAsync({ type: 'blob' });
  return blob;
}
